// apps/web/src/components/reports/campaign-performance/CampaignPerformanceFilters.tsx
/**
 * Campaign Performance — the filter drawer.
 *
 * Opens on arrival and holds the report closed until it is satisfied: Start
 * Date, End Date and Campaign are required, matching Team Performance's
 * drawer. The campaign picker is a picklist (the report is grouped BY
 * campaign), so — unlike team-performance — there is no dependent team list.
 */
import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react'
import { Dropdown } from 'primereact/dropdown'
import DualListSelect from '../../common/DualListSelect.js'
import { Calendar } from 'primereact/calendar'
import { SlidersHorizontal, RotateCcw } from 'lucide-react'

import ResourceDrawer from '../../admin/ResourceDrawer.js'
import {
  useCampaignPerformanceFilterOptions,
  type CampaignReportFilters,
} from '../../../hooks/useCampaignPerformanceReport.js'
import { useOrgTimezone } from '../../../hooks/useOrgTimezone.js'

const INTERVAL_OPTIONS = [
  { label: '30 Minutes', value: '30m' },
  { label: '60 Minutes Hourly', value: '60m' },
  { label: '24 Hours Day Wise', value: '1d' },
]

interface CampaignPerformanceFiltersProps {
  open: boolean
  onClose: () => void
  onRun: (filters: CampaignReportFilters) => void
  onReset: () => void
  running: boolean
}

/** Zero-pad a number to 2 digits — shared by the date and time formatters. */
function pad(n: number): string {
  return String(n).padStart(2, '0')
}

/** `YYYY-MM-DD` in LOCAL time — `toISOString()` would shift the day westward. */
function toDateString(d: Date | null): string {
  if (!d) return ''
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** `HH:MM` in local time; empty when unset — the backend defaults to the whole day. */
function toTimeString(d: Date | null): string {
  if (!d) return ''
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default function CampaignPerformanceFilters({
  open,
  onClose,
  onRun,
  onReset,
  running,
}: CampaignPerformanceFiltersProps): JSX.Element {
  const browserZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  )
  const { timezone: orgTimezone } = useOrgTimezone()

  // Start on the resolved timezone if it's already cached (no flash), or the
  // browser zone as a placeholder while the first fetch is in flight.
  const [timezone, setTimezone] = useState<string>(orgTimezone || browserZone)

  // Once the resolved timezone arrives, adopt it — but only if the user hasn't
  // manually changed the dropdown yet (timezone still matches the initial value).
  useEffect(() => {
    if (orgTimezone) setTimezone(orgTimezone)
  }, [orgTimezone])
  const [interval, setInterval] = useState<string>('1d')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [startTime, setStartTime] = useState<Date | null>(null)
  const [endTime, setEndTime] = useState<Date | null>(null)
  const [campaignIds, setCampaignIds] = useState<string[]>([])
  const [touched, setTouched] = useState(false)

  const { options } = useCampaignPerformanceFilterOptions()

  const timezoneOptions = useMemo(() => {
    const list = options.timezones.map((t) => ({
      label: t.offset ? `${t.label} (${t.offset})` : t.label,
      value: t.value,
    }))
    if (!list.some((t) => t.value === browserZone)) {
      list.unshift({ label: browserZone, value: browserZone })
    }
    return list
  }, [options.timezones, browserZone])

  const errors = {
    startDate: !startDate ? 'Start date is required' : '',
    endDate: !endDate
      ? 'End date is required'
      : startDate && endDate < startDate
        ? 'End date must be on or after start date'
        : '',
    campaignId: campaignIds.length === 0 ? 'At least one campaign is required' : '',
  }
  const valid = !errors.startDate && !errors.endDate && !errors.campaignId

  const handleRun = async (): Promise<void> => {
    setTouched(true)
    if (!valid) return
    onRun({
      timezone,
      interval,
      startDate: toDateString(startDate),
      endDate: toDateString(endDate),
      startTime: toTimeString(startTime),
      endTime: toTimeString(endTime),
      campaignIds,
    })
  }

  const handleReset = (): void => {
    setTimezone(orgTimezone)
    setInterval('1d')
    setStartDate(null)
    setEndDate(null)
    setStartTime(null)
    setEndTime(null)
    setCampaignIds([])
    setTouched(false)
    onReset()
  }

  const showError = (msg: string): JSX.Element | null =>
    touched && msg ? <small className="mt-1 block text-xs font-medium text-red-600">{msg}</small> : null

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={handleRun}
      saving={running}
      disabled={touched && !valid}
      width="xl"
      saveLabel={running ? 'Running…' : 'Run Report'}
      title="Report Filters"
    >
      <div className="kc3-form">
      <p className="mb-4 flex items-center gap-2 text-xs text-slate-500">
        <SlidersHorizontal className="h-3.5 w-3.5 text-[#3366FF]" aria-hidden="true" />
        Choose a window and campaign, then run the report. Times are optional — leave them empty for whole days.
      </p>

      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400">Reporting window</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Timezone">
          <Dropdown
            value={timezone}
            options={timezoneOptions}
            onChange={(e) => setTimezone(String(e.value))}
            filter
            placeholder="Select timezone"
            className="w-full"
          />
        </Field>

        <Field label="Interval">
          <Dropdown
            value={interval}
            options={INTERVAL_OPTIONS}
            onChange={(e) => setInterval(String(e.value))}
            placeholder="Select interval"
            className="w-full"
          />
        </Field>

        <Field label="Start Date" required>
          <Calendar
            value={startDate}
            onChange={(e) => setStartDate((e.value as Date | null) ?? null)}
            dateFormat="yy-mm-dd"
            showIcon
            placeholder="Select start date"
            className={`w-full ${touched && errors.startDate ? 'p-invalid' : ''}`}
            maxDate={endDate ?? undefined}
          />
          {showError(errors.startDate)}
        </Field>

        <Field label="Start Time">
          <Calendar
            value={startTime}
            onChange={(e) => setStartTime((e.value as Date | null) ?? null)}
            timeOnly
            hourFormat="24"
            showIcon
            icon="pi pi-clock"
            placeholder="00:00"
            className="w-full"
          />
        </Field>

        <Field label="End Date" required>
          <Calendar
            value={endDate}
            onChange={(e) => setEndDate((e.value as Date | null) ?? null)}
            dateFormat="yy-mm-dd"
            showIcon
            placeholder="Select end date"
            className={`w-full ${touched && errors.endDate ? 'p-invalid' : ''}`}
            minDate={startDate ?? undefined}
          />
          {showError(errors.endDate)}
        </Field>

        <Field label="End Time">
          <Calendar
            value={endTime}
            onChange={(e) => setEndTime((e.value as Date | null) ?? null)}
            timeOnly
            hourFormat="24"
            showIcon
            icon="pi pi-clock"
            placeholder="23:59"
            className="w-full"
          />
        </Field>

      </div>

      <p className="mb-3 mt-5 border-t border-slate-100 pt-4 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400">Scope</p>
      <div className={touched && errors.campaignId ? 'rounded-[3px] ring-1 ring-red-300' : ''}>
        <DualListSelect
          options={options.campaigns}
          selected={campaignIds}
          onChange={setCampaignIds}
          availableLabel="Campaigns"
          selectedLabel="Selected Campaigns"
          searchPlaceholder="Search campaigns…"
          height={180}
          emptyText="No campaigns available"
        />
      </div>
      <p className="mt-1 text-xs font-medium text-slate-500">
        Campaign <span aria-hidden className="text-red-500">*</span>
        {touched && errors.campaignId && <span className="ml-2 text-red-600">{errors.campaignId}</span>}
      </p>

      <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
        <p className="text-xs text-slate-500">
          {campaignIds.length > 0
            ? `${campaignIds.length} campaign${campaignIds.length === 1 ? '' : 's'} selected`
            : 'All campaigns in the picklist are available — select at least one.'}
        </p>
        <button
          type="button"
          onClick={handleReset}
          className="inline-flex items-center gap-1.5 rounded-[3px] border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
          Reset
        </button>
      </div>
      </div>
    </ResourceDrawer>
  )
}

function Field({
  label,
  required = false,
  children,
}: { label: string; required?: boolean; children: ReactNode }): JSX.Element {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {required && <span aria-hidden className="ml-1 text-red-500">*</span>}
      </label>
      {children}
    </div>
  )
}
